import { useMemo, useState } from 'react';
import { Navigate } from '@tanstack/react-router';
import { Pencil, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Separator } from '@ui/separator.tsx';
import { useUser } from '@r/api/auth.tsx';
import { useApiAdminrewardsGetCollection } from '@r/api/generated/client.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isStepLogicType } from '@admin/types/workspace/logic.ts';
import pointsTranslation from '@r/translations/points-translation.ts';
import type {
  LogicTypeStepWorkspaceReadModel,
  RewardGroupedRewardReadModel
} from '@r/api/generated/client.schemas.ts';
import { UpdateStepDialog } from '@admin/components/settings/steps/update-step-dialog.tsx';
import { UpdateStepRewardDialog } from '@admin/components/settings/steps/update-step-reward-dialog.tsx';
import { StepRewardsTable } from '@admin/components/settings/steps/step-rewards-table.tsx';
import { useRewardShopId } from '@admin/components/settings/steps/reward-product-utils.ts';

/** Carte palier : infos éditables + tableau des récompenses par typologie. */
function StepCard({
  step,
  rewards,
  onEdit,
  onEditReward
}: {
  step: LogicTypeStepWorkspaceReadModel;
  rewards: RewardGroupedRewardReadModel[];
  onEdit: (step: LogicTypeStepWorkspaceReadModel) => void;
  onEditReward: (reward: RewardGroupedRewardReadModel) => void;
}) {
  const { t } = useTranslation();
  const canEdit = step.id != null;

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        <div className="flex gap-4 items-start">
          <div className="flex justify-center items-center w-12 h-12 rounded-lg shrink-0 bg-primary/10">
            <TrendingUp className="w-6 h-6 text-primary" />
          </div>
          <div className="flex flex-1 gap-4 justify-between items-start min-w-0">
            <div className="flex-1 space-y-3 min-w-0">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold">{step.name}</h3>
                <p className="text-muted-foreground">
                  {pointsTranslation(t, step.stepPoint, true)}
                </p>
              </div>
              {step.description?.trim() ? (
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                  {step.description}
                </p>
              ) : null}
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-muted-foreground"
                aria-label={t('admin.settings.steps.action.edit')}
                onClick={() => onEdit(step)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            ) : null}
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <h4 className="text-base font-semibold">
            {t('admin.settings.steps.rewards.section.title')}
          </h4>
          <StepRewardsTable rewards={rewards} onEditReward={onEditReward} />
        </div>
      </CardContent>
    </Card>
  );
}

/** Page Réglages › Liste des paliers (redirige si la logique courante n’est pas STEP). */
export function SettingsStepsListPage() {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace, isWorkspacePending, isWorkspaceSuccess } = UseWorkspace(user);
  const logicType = workspace?.currentLogicWorkspace?.logic?.type;
  const isStepProgram = isStepLogicType(logicType);

  const [editingStep, setEditingStep] = useState<LogicTypeStepWorkspaceReadModel | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<RewardGroupedRewardReadModel | null>(null);
  const [editingRewardStep, setEditingRewardStep] =
    useState<LogicTypeStepWorkspaceReadModel | null>(null);
  const [isEditRewardDialogOpen, setIsEditRewardDialogOpen] = useState(false);

  const rewardsQuery = useApiAdminrewardsGetCollection({
    query: {
      enabled: isWorkspaceSuccess && isStepProgram
    }
  });

  const rewardShopQuery = useRewardShopId(isWorkspaceSuccess && isStepProgram);
  const rewardShopId = rewardShopQuery.data ?? null;

  const sortedSteps = useMemo(
    () =>
      [...(workspace?.currentLogicWorkspace?.steps ?? [])].sort(
        (a, b) => a.stepPoint - b.stepPoint
      ),
    [workspace?.currentLogicWorkspace?.steps]
  );

  const rewardsByStepId = useMemo(() => {
    const map = new Map<number, RewardGroupedRewardReadModel[]>();
    for (const group of rewardsQuery.data ?? []) {
      const stepId = group.step?.id;
      if (stepId != null) {
        map.set(stepId, group.rewards ?? []);
      }
    }
    return map;
  }, [rewardsQuery.data]);

  const handleEdit = (step: LogicTypeStepWorkspaceReadModel) => {
    setEditingStep(step);
    setIsEditDialogOpen(true);
  };

  const handleEditReward = (
    step: LogicTypeStepWorkspaceReadModel,
    reward: RewardGroupedRewardReadModel
  ) => {
    setEditingRewardStep(step);
    setEditingReward(reward);
    setIsEditRewardDialogOpen(true);
  };

  if (isWorkspaceSuccess && !isStepProgram) {
    return <Navigate to="/settings" />;
  }

  const isLoading = isWorkspacePending || (isStepProgram && rewardsQuery.isPending);

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('admin.settings.steps.page.title')}</h1>
        <p className="max-w-2xl font-light text-muted-foreground">
          {t('admin.settings.steps.description')}
        </p>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : sortedSteps.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground">
            {t('admin.settings.steps.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedSteps.map((step) => (
            <StepCard
              key={step.id ?? step.name}
              step={step}
              rewards={step.id != null ? (rewardsByStepId.get(step.id) ?? []) : []}
              onEdit={handleEdit}
              onEditReward={(reward) => handleEditReward(step, reward)}
            />
          ))}
        </div>
      )}

      <UpdateStepDialog
        step={editingStep}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={() => setEditingStep(null)}
      />

      <UpdateStepRewardDialog
        reward={editingReward}
        step={editingRewardStep}
        rewardShopId={rewardShopId}
        rewardShopLoading={rewardShopQuery.isPending}
        isOpen={isEditRewardDialogOpen}
        onOpenChange={setIsEditRewardDialogOpen}
        onSuccess={() => {
          setEditingReward(null);
          setEditingRewardStep(null);
        }}
      />
    </div>
  );
}
